import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AppState } from './../../../../App.state';
import { NovoDashboardService } from 'src/app/base/services/novo-dashboard.service';
import { DialogService } from 'src/app/global/services/dialog.service';
import { QueryOptions } from 'src/app/global/domain/query.options';
import { ParcelasPagasVencidas } from 'src/app/base/domain/dto/parcelas-pagas-vencidas.dto';

Chart.register(...registerables);

@Component({
  selector: 'app-dashboard-parcelas-pagas-vencidas',
  templateUrl: './dashboard-parcelas-pagas-vencidas.component.html',
  styleUrls: ['./dashboard-parcelas-pagas-vencidas.component.css']
})
export class DashboardParcelasPagasVencidasComponent implements OnInit, OnDestroy {

  @ViewChild('graficoParcelasPagasVencidas', { static: true })
  canvas!: ElementRef<HTMLCanvasElement>;

  estado: string = '';
  rota: string = '';
  posto: string = '';
  nomeCliente: string = '';
  dataInicio: string = '';
  dataFim: string = '';
  rotasDisponiveis: string[] = [];
  postosDisponiveis: string[] = [];
  queryOptions = new QueryOptions({ pageSize: 100 });
  data: ParcelasPagasVencidas[] = [];
  chart?: Chart;
  selectedClient?: ParcelasPagasVencidas;

  filteredData: ParcelasPagasVencidas[] = [];
  paginatedData: ParcelasPagasVencidas[] = [];
  totalPages: number = 1;
  currentPage: number = 1;
  pageSize: number = 10;

  private searchInput$ = new Subject<string>();

  constructor(
    private service: NovoDashboardService,
    private appState: AppState,
    public dialogService: DialogService
  ) { }

  ngOnInit(): void {
    this.loadFilters();
    this.loadDashboard();

    this.searchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe((term: string) => {
      this.nomeCliente = term;
      this.currentPage = 1;
      this.applyFilter();
    });
  }

  ngOnDestroy(): void {
    this.searchInput$.complete();
  }

  onSearchInput(term: string): void {
    this.searchInput$.next(term);
  }

  private loadFilters(): void {
    this.service.findAllRotas().subscribe(res => this.rotasDisponiveis = res);
    this.service.findAllPostos().subscribe(res => this.postosDisponiveis = res);
  }

  public loadDashboard(): void {
    this.appState.isLoading = true;
    const dInicio = (this.dataInicio && this.dataInicio.length === 10) ? `${this.dataInicio}T00:00:00` : this.dataInicio;
    const dFim = (this.dataFim && this.dataFim.length === 10) ? `${this.dataFim}T23:59:59` : this.dataFim;

    this.queryOptions.params = {
      estado: this.estado,
      rota: this.rota,
      posto: this.posto,
      nomeCliente: this.nomeCliente,
      dataInicio: dInicio,
      dataFim: dFim
    };

    this.service.findAllParcelasPagasVencidas(this.queryOptions).subscribe({
      next: (response: ParcelasPagasVencidas[]) => {
        this.data = response;
        this.currentPage = 1;
        this.selectedClient = undefined; // Reset selection on new search
        this.applyFilter();
        this.updateAvailableFilters(response);
        this.createChart();
        this.appState.isLoading = false;
      },
      error: () => {
        this.dialogService.feedbackError("Erro ao carregar o dashboard de parcelas.");
        this.appState.isLoading = false;
      }
    });
  }

  private applyFilter(): void {
    const term = (this.nomeCliente || '').toLowerCase().trim();
    this.filteredData = !term ? this.data : this.data.filter(i =>
      (i.nomeCliente || '').toLowerCase().includes(term) ||
      (i.contratoId?.toString().includes(term))
    );
    this.totalPages = Math.ceil(this.filteredData.length / this.pageSize) || 1;
    this.updatePage();
  }

  private updatePage(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    this.paginatedData = this.filteredData.slice(start, start + this.pageSize);
  }

  private createChart(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    // Se houver um cliente selecionado, mostrar apenas o dado dele
    if (this.selectedClient) {
      const labels = ['Pagas (Dia)', 'Pagas (Atraso)', 'Vencidas', 'Abertas'];
      const data = [
        this.selectedClient.pagasEmDia || 0,
        this.selectedClient.pagasEmAtraso || 0,
        this.selectedClient.vencidas || 0,
        this.selectedClient.abertas || 0
      ];

      this.chart = new Chart(this.canvas.nativeElement, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: `Cliente: ${this.selectedClient.nomeCliente}`,
            data,
            backgroundColor: ['#4caf50', '#8bc34a', '#f44336', '#ff9800']
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: { display: true, text: `Detalhamento: ${this.selectedClient.nomeCliente}` }
          }
        }
      });
      return;
    }

    // Agrupar dados por Posto para o gráfico empilhado
    const grupos = Array.from(new Set(this.data.map(i => i.posto || 'Geral')));
    const labels = grupos.length > 5 ? grupos.slice(0, 5) : grupos; // Limitar para não poluir

    const dataPagasDia = labels.map(g => this.data.filter(i => (i.posto || 'Geral') === g).reduce((acc, curr) => acc + (curr.pagasEmDia || 0), 0));
    const dataPagasAtraso = labels.map(g => this.data.filter(i => (i.posto || 'Geral') === g).reduce((acc, curr) => acc + (curr.pagasEmAtraso || 0), 0));
    const dataVencidas = labels.map(g => this.data.filter(i => (i.posto || 'Geral') === g).reduce((acc, curr) => acc + (curr.vencidas || 0), 0));
    const dataAbertas = labels.map(g => this.data.filter(i => (i.posto || 'Geral') === g).reduce((acc, curr) => acc + (curr.abertas || 0), 0));

    const datasets = [
      { label: 'Pagas (Dia)', data: dataPagasDia, backgroundColor: '#4caf50' },
      { label: 'Pagas (Atraso)', data: dataPagasAtraso, backgroundColor: '#8bc34a' },
      { label: 'Vencidas', data: dataVencidas, backgroundColor: '#f44336' },
      { label: 'Abertas', data: dataAbertas, backgroundColor: '#ff9800' }
    ];

    this.chart = new Chart(this.canvas.nativeElement, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { stacked: true },
          y: { stacked: true, beginAtZero: true }
        },
        plugins: {
          tooltip: {
            callbacks: {
              footer: (items) => {
                const total = items.reduce((acc, item) => acc + (item.raw as number), 0);
                return `Total: ${total}`;
              }
            }
          }
        }
      }
    });
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePage();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePage();
    }
  }

  selectClient(client: ParcelasPagasVencidas): void {
    this.selectedClient = client;
    this.createChart();
    // Scroll to chart
    this.canvas.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  clearSelection(): void {
    this.selectedClient = undefined;
    this.createChart();
  }

  private updateAvailableFilters(data: ParcelasPagasVencidas[]): void {
    if (!data || data.length === 0) return;

    // Extrai rotas únicas dos dados se a lista estiver vazia
    if (this.rotasDisponiveis.length === 0) {
      const rotas = data.map(i => i.rota).filter(r => r != null && r !== '');
      this.rotasDisponiveis = Array.from(new Set(rotas)).sort();
    }

    // Extrai postos únicos dos dados se a lista estiver vazia
    if (this.postosDisponiveis.length === 0) {
      const postos = data.map(i => i.posto).filter(p => p != null && p !== '');
      this.postosDisponiveis = Array.from(new Set(postos)).sort();
    }
  }
}
